import type { StrapiSurveyGridRow } from '../../api/strapiCrm';

export function parseListOrder(value: unknown): number | null {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.floor(n);
}

export function jobNumberFromSurveyRow(row: {
  job_number?: string | null;
  work_order_label?: string | null;
}): string {
  const job = String(row.job_number ?? '').trim();
  if (job) return job;
  const label = String(row.work_order_label ?? '').trim();
  if (!label) return '';
  return label.split('·')[0]?.trim() ?? '';
}

/** Kode sampel: `${job_number}-${list_order}`. Titik dibatalkan tidak mendapat nomor. */
export function formatBeritaAcaraKodeSampel(
  jobNumber: string,
  listOrder: number | null | undefined,
): string {
  const job = String(jobNumber ?? '').trim();
  const order = parseListOrder(listOrder);
  if (!job || order == null) return '';
  return `${job}-${order}`;
}

export function surveyRowIsCancelled(row: Pick<StrapiSurveyGridRow, 'status' | 'cancelled_at'>): boolean {
  return row.status === 'cancelled' || Boolean(String(row.cancelled_at ?? '').trim());
}

export function surveyRowKodeSampel(row: StrapiSurveyGridRow): string {
  if (surveyRowIsCancelled(row)) return '';
  return formatBeritaAcaraKodeSampel(jobNumberFromSurveyRow(row), row.list_order);
}

export function sortSurveyRowsByListOrder(rows: StrapiSurveyGridRow[]): StrapiSurveyGridRow[] {
  return [...rows].sort((a, b) => {
    const ao = parseListOrder(a.list_order);
    const bo = parseListOrder(b.list_order);
    if (ao != null && bo != null && ao !== bo) return ao - bo;
    if (ao != null && bo == null) return -1;
    if (ao == null && bo != null) return 1;
    const plan = String(a.sampling_plan_document_id).localeCompare(String(b.sampling_plan_document_id));
    if (plan !== 0) return plan;
    return a.point_index - b.point_index;
  });
}

export function canDragSurveyLetterRow(row: StrapiSurveyGridRow): boolean {
  if (surveyRowIsCancelled(row)) return false;
  return Boolean(String(row.berita_acara_document_id ?? '').trim());
}

export function reorderSurveyLetterRows(
  rows: StrapiSurveyGridRow[],
  fromRowKey: string,
  toRowKey: string,
): { rows: StrapiSurveyGridRow[]; orderedDocumentIds: string[] } | null {
  if (!fromRowKey || !toRowKey || fromRowKey === toRowKey) return null;
  const letterRows = rows.filter(canDragSurveyLetterRow);
  const fromIdx = letterRows.findIndex((row) => row.row_key === fromRowKey);
  let toIdx = letterRows.findIndex((row) => row.row_key === toRowKey);
  if (fromIdx < 0) return null;
  if (toIdx < 0) {
    const target = rows.find((row) => row.row_key === toRowKey);
    if (!target) return null;
    toIdx = letterRows.length - 1;
  }
  if (fromIdx === toIdx) return null;

  const nextLetters = [...letterRows];
  const [moved] = nextLetters.splice(fromIdx, 1);
  nextLetters.splice(toIdx, 0, moved);
  const orderByKey = new Map(nextLetters.map((row, index) => [row.row_key, index + 1]));
  const nextRows = sortSurveyRowsByListOrder(
    rows.map((row) => {
      const listOrder = orderByKey.get(row.row_key);
      return listOrder != null ? { ...row, list_order: listOrder } : row;
    }),
  );
  const orderedDocumentIds = nextLetters
    .map((row) => String(row.berita_acara_document_id ?? '').trim())
    .filter(Boolean);
  return { rows: nextRows, orderedDocumentIds };
}
